import { Capacitor } from '@capacitor/core';
import { saveToken, removeToken } from './tokenStorage';

const isElectron = typeof window !== 'undefined' && (
    navigator.userAgent.toLowerCase().includes('electron') ||
    window.location.protocol === 'file:'
);

const isViteDev = import.meta.env.DEV;

export const API_BASE_URL = (() => {
    if (typeof window === 'undefined') {
        return 'https://eeepc.tail142a64.ts.net/api';
    }
    
    // Si l'application est chargée via un protocole HTTP/HTTPS classique
    // (navigateur ou Electron pointant vers le serveur)
    if (window.location.protocol === 'http:' || window.location.protocol === 'https:') {
        // En mode dev local sur le PC, si on charge via localhost, on redirige vers l'IP locale du serveur
        if (isViteDev && window.location.hostname === 'localhost') {
            return 'http://192.168.1.21/api';
        }
        return `${window.location.protocol}//${window.location.host}/api`;
    }
    
    // Cas où le protocole n'est pas http/https (par ex. file:// dans Electron, ou capacitor://localhost sur mobile)
    if (Capacitor.isNativePlatform()) {
        return 'https://eeepc.tail142a64.ts.net/api';
    }
    
    if (isElectron) {
        return 'https://eeepc.tail142a64.ts.net/api';
    }
    
    return 'https://eeepc.tail142a64.ts.net/api';
})();

const getHeaders = (extraHeaders: Record<string, string> = {}) => {
    const token = localStorage.getItem('nexus_token');
    const headers: Record<string, string> = {
        'ngrok-skip-browser-warning': 'true',
        ...extraHeaders
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

export const localApi = {
    async login(email: string, password: string) {
        const response = await fetch(`${API_BASE_URL}/login.php`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true'
            },
            body: JSON.stringify({ email, password })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de connexion');
        }
        await saveToken(data.session.access_token);
        return data;
    },

    async getSession() {
        const token = localStorage.getItem('nexus_token');
        if (!token) return null;
        
        const response = await fetch(`${API_BASE_URL}/session.php`, {
            headers: { 
                'Authorization': `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            }
        });
        const data = await response.json();
        if (!response.ok || !data.session) {
            await removeToken();
            return null;
        }
        return data.session;
    },

    async logout() {
        await removeToken();
    },

    async getLtvs() {
        const response = await fetch(`${API_BASE_URL}/security.php?action=ltvs`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async createLtv(ltv: any) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=create_ltv`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(ltv)
        });
        return response.json();
    },

    async importLtvs(ltvs: any[]) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=import_ltvs`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(ltvs)
        });
        return response.json();
    },

    async convertLtvPdf(file: File) {
        const formData = new FormData();
        formData.append('pdf_file', file);
        const response = await fetch(`${API_BASE_URL}/convert_ltv_pdf.php`, {
            method: 'POST',
            headers: getHeaders(),
            body: formData
        });
        return response.json();
    },

    async deleteLtv(id: string) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=delete_ltv&id=${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return response.json();
    },

    async getBulletins() {
        const response = await fetch(`${API_BASE_URL}/security.php?action=bulletins`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async uploadBulletin(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${API_BASE_URL}/security.php?action=upload_bulletin`, {
            method: 'POST',
            headers: getHeaders(),
            body: formData
        });
        return response.json();
    },

    async deleteBulletin(id: string) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=delete_bulletin&id=${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return response.json();
    },

    async sendBlackboxEvent(event: any) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=blackbox`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(event)
        });
        return response.json();
    },

    async getBlackboxEvents() {
        const response = await fetch(`${API_BASE_URL}/security.php?action=blackbox`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async convertMissionsPdf(file: File): Promise<string[][]> {
        const formData = new FormData();
        formData.append('pdf_file', file);
        const response = await fetch(`${API_BASE_URL}/convert_pdf.php`, {
            method: 'POST',
            headers: getHeaders(),
            body: formData
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || "Erreur lors du traitement du fichier PDF.");
        }
        const res = await response.json();
        return res.grid;
    },

    async getDriverBulletins() {
        const response = await fetch(`${API_BASE_URL}/driver_bulletins.php?action=list`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async getNextBulletinNumber() {
        const response = await fetch(`${API_BASE_URL}/driver_bulletins.php?action=next_num`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.next_number || '01';
    },

    async saveDriverBulletin(bulletin: any) {
        const response = await fetch(`${API_BASE_URL}/driver_bulletins.php?action=save`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(bulletin)
        });
        const data = await response.json();
        return data;
    },

    async getMissions() {
        const response = await fetch(`${API_BASE_URL}/missions.php`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return (data.data || []).map((m: any) => ({
            id: m.id,
            trainNumber: m.train_number,
            lineName: m.line_name,
            stops: m.stops
        }));
    },

    async createMission(mission: { trainNumber: string, lineName: string, stops: any[] }) {
        const response = await fetch(`${API_BASE_URL}/missions.php`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                train_number: mission.trainNumber,
                line_name: mission.lineName,
                stops: mission.stops
            })
        });
        return response.json();
    },

    async importMissions(missions: any[]) {
        const payload = missions.map(m => ({
            id: m.id,
            train_number: m.train_number || m.trainNumber,
            line_name: m.line_name || m.lineName,
            stops: m.stops
        }));
        const response = await fetch(`${API_BASE_URL}/missions.php?action=import`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload)
        });
        return response.json();
    },

    async updateMission(mission: { id: string, trainNumber: string, lineName: string, stops: any[] }) {
        const response = await fetch(`${API_BASE_URL}/missions.php?action=update&id=${mission.id}`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                train_number: mission.trainNumber,
                line_name: mission.lineName,
                stops: mission.stops
            })
        });
        return response.json();
    },

    async deleteMission(id: string) {
        const response = await fetch(`${API_BASE_URL}/missions.php?id=${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return response.json();
    },

    async getGpsPositions() {
        const response = await fetch(`${API_BASE_URL}/gps.php`, {
            headers: getHeaders()
        });
        const data = await response.json();
        const raw = data.data || [];
        return raw.map((p: any) => ({
            session_id: p.session_id,
            latitude: p.lat,
            longitude: p.long,
            mission_name: p.mission_name,
            updated_at: p.updated_at
        }));
    },

    async updateGpsPosition(sessionId: string, lat: number, long: number, missionName: string) {
        const response = await fetch(`${API_BASE_URL}/gps.php`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ session_id: sessionId, lat, long, mission_name: missionName })
        });
        return response.json();
    },

    async deleteGpsPosition(sessionId: string) {
        const response = await fetch(`${API_BASE_URL}/gps.php?session_id=${sessionId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return response.json();
    },

    async getStopRequests(trainNumber: string) {
        const response = await fetch(`${API_BASE_URL}/stops.php?train_number=${encodeURIComponent(trainNumber)}`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async createStopRequest(trainNumber: string, stationName: string, requestedBy: string) {
        const response = await fetch(`${API_BASE_URL}/stops.php`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ train_number: trainNumber, station_name: stationName, requested_by: requestedBy })
        });
        return response.json();
    },

    async deleteStopRequest(trainNumber: string, stationName?: string) {
        let url = `${API_BASE_URL}/stops.php?train_number=${encodeURIComponent(trainNumber)}`;
        if (stationName) {
            url += `&station_name=${encodeURIComponent(stationName)}`;
        }
        const response = await fetch(url, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return response.json();
    },

    async getUsers() {
        const response = await fetch(`${API_BASE_URL}/users.php?action=list`, {
            headers: getHeaders()
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de récupération des utilisateurs');
        }
        return data.data || [];
    },

    async createUser(payload: { email: string, password: string, role: string }) {
        const response = await fetch(`${API_BASE_URL}/users.php?action=create`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de création de l\'utilisateur');
        }
        return data;
    },

    async deleteUser(userId: string) {
        const response = await fetch(`${API_BASE_URL}/users.php?action=delete`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ user_id: userId })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de suppression de l\'utilisateur');
        }
        return data;
    },

    async updateUserPassword(userId: string, newPassword: string) {
        const response = await fetch(`${API_BASE_URL}/users.php?action=update_password`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ user_id: userId, new_password: newPassword })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de mise à jour du mot de passe');
        }
        return data;
    },

    async updateUserZones(userId: string, zones: string) {
        const response = await fetch(`${API_BASE_URL}/users.php?action=update_zones`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ user_id: userId, zones })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de mise à jour des zones');
        }
        return data;
    },

    async uploadPlanning(file: File, zone: string) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('zone', zone);
        const response = await fetch(`${API_BASE_URL}/security.php?action=upload_planning`, {
            method: 'POST',
            headers: getHeaders(),
            body: formData
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur lors du téléversement du planning');
        }
        return data;
    },

    async getIncidents() {
        const response = await fetch(`${API_BASE_URL}/security.php?action=get_incidents`, {
            method: 'GET',
            headers: getHeaders({ 'Content-Type': 'application/json' })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur de chargement des incidents');
        }
        return data.data || [];
    },

    async reportIncident(incident: { id: string; type: string; pk: number; timestamp: number; description: string }) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=report_incident`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(incident)
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur lors du signalement de l\'incident');
        }
        return data;
    },

    async resolveIncident(id: string) {
        const response = await fetch(`${API_BASE_URL}/security.php?action=resolve_incident`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ id })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Erreur lors de la résolution de l\'incident');
        }
        return data;
    },

    async checkEmergencyStop(trainNumber: string) {
        const response = await fetch(`${API_BASE_URL}/stops.php?action=check_emergency&train_number=${encodeURIComponent(trainNumber)}`, {
            headers: getHeaders()
        });
        return await response.json();
    },

    async triggerEmergencyStop(trainNumber: string, requestedBy: string) {
        const response = await fetch(`${API_BASE_URL}/stops.php?action=trigger_emergency`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ train_number: trainNumber, requested_by: requestedBy })
        });
        return await response.json();
    },

    async cancelEmergencyStop(trainNumber: string) {
        const response = await fetch(`${API_BASE_URL}/stops.php?action=cancel_emergency&train_number=${encodeURIComponent(trainNumber)}`, {
            headers: getHeaders()
        });
        return await response.json();
    },

    async updateStopRequestStatus(trainNumber: string, stationName: string, status: 'accepted' | 'rejected') {
        const response = await fetch(`${API_BASE_URL}/stops.php?action=update_status`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ train_number: trainNumber, station_name: stationName, status })
        });
        return await response.json();
    },

    async getConductorLeaves(): Promise<ConductorLeave[]> {
        const response = await fetch(`${API_BASE_URL}/leaves.php`, {
            headers: getHeaders()
        });
        const data = await response.json();
        return data.data || [];
    },

    async saveConductorLeave(leave: Partial<ConductorLeave>) {
        const response = await fetch(`${API_BASE_URL}/leaves.php`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(leave)
        });
        return await response.json();
    },

    async updateConductorLeaveStatus(id: string, status: 'PENDING' | 'VALIDATED' | 'REJECTED', comment?: string) {
        const response = await fetch(`${API_BASE_URL}/leaves.php?action=update_status`, {
            method: 'POST',
            headers: getHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ id, status, comment })
        });
        return await response.json();
    },

    async deleteConductorLeave(id: string) {
        const response = await fetch(`${API_BASE_URL}/leaves.php?id=${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return await response.json();
    }
};

export interface ConductorLeave {
    id: string;
    conductor_id: string;
    conductor_name: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    status: 'PENDING' | 'VALIDATED' | 'REJECTED';
    comment?: string;
    created_at?: string;
    updated_at?: string;
}

